import { FiGitMerge } from 'react-icons/fi';

export const presentPerfectAdvData = {
  courseId: 'present-perfect-adv',
  courseType: 'grammar',
  title: 'Present Perfect vs Past Simple',
  icon: FiGitMerge,
  overview: {
    impact: 'The most commonly tested tense distinction in IELTS',
    focus: 'When to use present perfect vs past simple',
    uses: 'All sections — especially Writing and Speaking',
    duration: '25 minutes'
  },
  sections: [
    {
      title: 'Key Differences',
      content: `<p>This is the most important tense distinction for IELTS:</p>`,
      table: {
        headers: [
          'Present Perfect',
          'Past Simple'
        ],
        rows: [
          [
            'No specific time: \'I\'ve been to Japan.\'',
            'Specific time: \'I went to Japan in 2022.\''
          ],
          [
            'Experience up to now: \'She\'s written 3 books.\'',
            'Completed event: \'She wrote her first book in 2018.\''
          ],
          [
            'Unfinished time: \'I\'ve worked 8 hours today.\'',
            'Finished time: \'I worked 8 hours yesterday.\''
          ],
          [
            'Still relevant: \'I\'ve lost my keys.\' (still lost)',
            'No current relevance: \'I lost my keys but found them.\''
          ]
        ]
      }
    },
    {
      title: 'Signal Words',
      content: `<p>These words usually indicate which tense to use:</p>`,
      table: {
        headers: [
          'Present Perfect',
          'Past Simple'
        ],
        rows: [
          [
            'ever, never',
            'yesterday, last week/month/year'
          ],
          [
            'already, yet, just',
            'ago, in 2020'
          ],
          [
            'since, for',
            'when I was young'
          ],
          [
            'so far, up to now',
            'at that time'
          ],
          [
            'recently, lately',
            'the other day'
          ]
        ]
      }
    },
    {
      title: 'Common IELTS Mistakes',
      content: `<p>Avoid these errors that reduce your grammar score:</p>`,
      points: [
        'WRONG: \'I have visited Paris last year.\' → RIGHT: \'I visited Paris last year.\'',
        'WRONG: \'I went there many times.\' → RIGHT: \'I have been there many times.\'',
        'WRONG: \'Since I am a child...\' → RIGHT: \'Since I was a child...\'',
        'WRONG: \'I am living here for 5 years.\' → RIGHT: \'I have lived here for 5 years.\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Choose the Correct Tense',
      type: 'select',
      instructions: 'Select present perfect or past simple.',
      questions: [
        {
          text: 'I ___ (live) in this city since 2018.',
          options: [
            'have lived',
            'lived',
            'am living'
          ],
          answer: 'have lived',
          explanation: '\'Since 2018\' (unfinished time) → present perfect.'
        },
        {
          text: 'She ___ (graduate) from university in 2021.',
          options: [
            'graduated',
            'has graduated',
            'was graduating'
          ],
          answer: 'graduated',
          explanation: 'Specific past time (in 2021) → past simple.'
        },
        {
          text: '___ you ever ___ (try) Japanese food?',
          options: [
            'Have, tried',
            'Did, try',
            'Were, trying'
          ],
          answer: 'Have, tried',
          explanation: 'Life experience (\'ever\') → present perfect.'
        },
        {
          text: 'I ___ (not see) him yesterday.',
          options: [
            'didn\'t see',
            'haven\'t seen',
            'wasn\'t seeing'
          ],
          answer: 'didn\'t see',
          explanation: 'Specific past time (yesterday) → past simple.'
        }
      ]
    },
    {
      title: 'Since vs For',
      type: 'select',
      instructions: 'Choose the correct word: "since" or "for".',
      questions: [
        {
          text: 'I have lived here ___ 2020.',
          options: [
            'since',
            'for',
            'from'
          ],
          answer: 'since',
          explanation: '\'Since\' is used with a specific point in time (2020).'
        },
        {
          text: 'She has been studying English ___ 5 years.',
          options: [
            'for',
            'since',
            'during'
          ],
          answer: 'for',
          explanation: '\'For\' is used with a duration of time (5 years).'
        },
        {
          text: 'We haven\'t spoken ___ Monday.',
          options: [
            'since',
            'for',
            'from'
          ],
          answer: 'since',
          explanation: '\'Since\' is used with a specific point in time (Monday).'
        },
        {
          text: 'They have been waiting ___ a long time.',
          options: [
            'for',
            'since',
            'during'
          ],
          answer: 'for',
          explanation: '\'For\' is used with a period/duration (a long time).'
        }
      ]
    },
    {
      title: 'Present Perfect vs Past Simple',
      type: 'select',
      instructions: 'Choose the correct tense for each context.',
      questions: [
        {
          text: 'I ___ three countries so far this year.',
          options: [
            'have visited',
            'visited',
            'was visiting'
          ],
          answer: 'have visited',
          explanation: '\'So far this year\' indicates unfinished time → present perfect.'
        },
        {
          text: 'He ___ to London when he was a child.',
          options: [
            'moved',
            'has moved',
            'had moved'
          ],
          answer: 'moved',
          explanation: '\'When he was a child\' is a specific finished time → past simple.'
        },
        {
          text: 'I ___ my keys. Can you help me find them?',
          options: [
            'have lost',
            'lost',
            'was losing'
          ],
          answer: 'have lost',
          explanation: 'The result is still relevant now (keys are still missing) → present perfect.'
        },
        {
          text: 'We ___ to that restaurant last Friday.',
          options: [
            'went',
            'have gone',
            'have been'
          ],
          answer: 'went',
          explanation: '\'Last Friday\' is a specific past time → past simple.'
        }
      ]
    },
    {
      title: 'Complete the Sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank using the present perfect with just, already, yet, or still.',
      questions: [
        {
          text: 'I have ___ finished my homework. (= a moment ago)',
          answer: 'just',
          explanation: '\'Just\' means a very short time ago and is used with present perfect.'
        },
        {
          text: 'She has ___ read that book, so she doesn\'t need another copy.',
          answer: 'already',
          explanation: '\'Already\' means something happened sooner than expected.'
        },
        {
          text: 'Have you finished your essay ___? (= up to now)',
          answer: 'yet',
          explanation: '\'Yet\' is used in questions and negatives to ask about expected actions.'
        },
        {
          text: 'He ___ hasn\'t replied to my email. (= continuing situation)',
          answer: 'still',
          explanation: '\'Still\' emphasizes that an expected change has not happened.'
        }
      ]
    }
  ],
  tips: [
    'If there\'s a specific past time → past simple',
    'If it connects to NOW or no time given → present perfect',
    '\'Since\' + point in time; \'For\' + duration',
    'This distinction is HEAVILY tested in IELTS — master it'
  ],
  dailyPractice: [
    'Practice present perfect vs past simple concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Present Perfect vs Past Simple: The most tested grammar distinction in IELTS. Present perfect for unfinished time/experience/current relevance; past simple for specific past times.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...presentPerfectAdvData,
      title: presentPerfectAdvData.title + ' (Foundations)',
      overview: { ...presentPerfectAdvData.overview, duration: '15-20 minutes' },
      sections: presentPerfectAdvData.sections.slice(0, Math.max(2, Math.ceil(presentPerfectAdvData.sections.length / 2))),
      exercises: presentPerfectAdvData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...presentPerfectAdvData,
      title: presentPerfectAdvData.title + ' (Advanced)',
      overview: { ...presentPerfectAdvData.overview, duration: '30-35 minutes' },
      sections: presentPerfectAdvData.sections,
      exercises: presentPerfectAdvData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(presentPerfectAdvData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return presentPerfectAdvData;
}
